import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, CheckCircle2, MessageSquare, FileText, CalendarCheck, Lightbulb, Truck } from "lucide-react";
import { api } from "../lib/api";
import { useSeo, useSite } from "../lib/site";
import { CITIES, AREA_LINE, TRAVEL_NOTE, OUTSIDE_NOTE } from "../lib/cities";
import { PageHero, CallButton, TextButton, QuoteButton, SeniorTerms, FinalCta } from "../components/site/Blocks";
import { FaqList } from "./Home";
import { NotFound } from "./Info";

function Ctas({ prefix }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row">
      <QuoteButton testid={`${prefix}-quote-btn`} />
      <CallButton className="btn-outline" testid={`${prefix}-call-btn`} />
      <TextButton className="btn-outline" testid={`${prefix}-text-btn`} />
    </div>
  );
}

function ZipChecker() {
  const [zip, setZip] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const check = async (e) => {
    e.preventDefault();
    if (!/^\d{5}$/.test(zip)) { setRes(null); return setErr("Please enter a 5-digit ZIP code."); }
    setErr("");
    const r = await api.get("/public/area-check", { params: { zip } }).catch(() => null);
    setRes(r ? r.data : null);
  };
  return (
    <form onSubmit={check} className="card bg-[#F5F5F5]" noValidate data-testid="zip-check-form">
      <label htmlFor="zip-check" className="field-label">Check your ZIP code</label>
      <div className="flex gap-3">
        <input id="zip-check" className="input" inputMode="numeric" maxLength={5} value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))} aria-invalid={!!err} aria-describedby="zip-check-msg" data-testid="zip-check-input" />
        <button className="btn-dark" data-testid="zip-check-btn">Check</button>
      </div>
      <div id="zip-check-msg" role="status" className="mt-3 text-base">
        {err && <p className="field-error">{err}</p>}
        {res?.in_area && <p className="font-bold text-green-800" data-testid="zip-in-area">{res.zip} is in our {res.city_name} service area.</p>}
        {res && !res.in_area && <p className="font-bold" data-testid="zip-out-area">{res.zip} isn't on our usual list. {OUTSIDE_NOTE} You can also send a request and we'll review it personally.</p>}
      </div>
      <p className="mt-3 text-sm text-neutral-600">{TRAVEL_NOTE}</p>
    </form>
  );
}

export function ServiceAreas() {
  useSeo("Service Areas", "One Punch Services serves Albuquerque, Rio Rancho, Los Lunas, Corrales and Santa Fe, New Mexico with handyman help, junk removal, yard cleanup, property cleanouts and senior home support.");
  return (
    <div data-testid="service-areas-page">
      <PageHero eyebrow="Service areas" title="Where we work">{AREA_LINE} Our business is based in Albuquerque.</PageHero>
      <section className="wrap py-14">
        <p className="max-w-3xl text-lg leading-relaxed text-neutral-800">In each of these cities you can request handyman help, junk removal, yard cleanup, property cleanouts, furniture assembly and senior home support — all within our offered service scope. Availability can vary by city, so your preferred date is a request until we confirm it with you.</p>
        <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="city-list">
          {CITIES.map((c) => (
            <li key={c.slug}>
              <Link to={`/service-areas/${c.slug}`} className="card group flex h-full flex-col hover:border-[#D9232E]" data-testid={`city-card-${c.slug}`}>
                <MapPin className="h-8 w-8 text-[#D9232E]" aria-hidden="true" />
                <h2 className="mt-3 text-3xl font-bold uppercase">{c.name}</h2>
                <p className="mt-2 flex-1 text-base text-neutral-700">{c.headline}</p>
                <span className="mt-4 font-bold text-[#B81B25] group-hover:underline">Services in {c.name} →</span>
              </Link>
            </li>
          ))}
          <li className="card flex flex-col justify-center bg-[#171717] text-white" data-testid="city-card-other">
            <p className="text-2xl font-bold uppercase">Somewhere nearby?</p>
            <p className="mt-2 text-base text-neutral-300">{OUTSIDE_NOTE}</p>
            <CallButton className="btn-red mt-4" testid="areas-other-call-btn" />
          </li>
        </ul>
      </section>
      <section className="wrap grid gap-10 pb-16 md:grid-cols-2">
        <ZipChecker />
        <div>
          <h2 className="text-2xl font-bold uppercase">Ready when you are</h2>
          <p className="mt-2 mb-6 text-base text-neutral-700">Tell us what you need, and we'll send a clear written estimate. Nothing is scheduled or charged until you approve it.</p>
          <Ctas prefix="areas" />
        </div>
      </section>
    </div>
  );
}

const STEPS = [[MessageSquare, "Tell us", "Use the online form, call, or text us photos."], [FileText, "Get a written estimate", "Itemized pricing, including any applicable travel charge."], [CalendarCheck, "Approve & schedule", "We confirm a time with you after you approve."]];

export default function CityPage() {
  const { city: slug } = useParams();
  const c = CITIES.find((x) => x.slug === slug);
  const { site } = useSite();
  useSeo(c ? c.title : "Service area", c?.seo);
  if (!c) return <NotFound />;
  const zips = site.service_area?.cities?.find((x) => x.key === c.key)?.zips || [];
  return (
    <div data-testid={`city-page-${c.slug}`}>
      <PageHero eyebrow={`${c.name}, New Mexico`} title={c.title}>
        <p>{c.headline}</p>
        <div className="mt-6"><Ctas prefix="city-hero" /></div>
      </PageHero>
      <section className="wrap grid gap-10 py-14 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-4 text-lg leading-relaxed text-neutral-800">{c.intro.map((p) => <p key={p.slice(0, 20)}>{p}</p>)}</div>
        <div className="rounded-lg border-4 border-[#D9232E] bg-white p-6" data-testid="city-senior-box">
          <p className="font-display text-4xl font-extrabold uppercase leading-none text-[#D9232E]">Seniors Save 20% for Life</p>
          <p className="mt-2 text-base font-bold">On eligible services, in {c.name} and every city we serve.</p>
          <div className="mt-3"><SeniorTerms compact /></div>
          <Link to="/senior-home-support" className="mt-3 inline-block font-bold text-[#B81B25] underline" data-testid="city-senior-link">How the senior discount works</Link>
        </div>
      </section>
      <section className="bg-[#F5F5F5]">
        <div className="wrap py-14">
          <h2 className="h2">How we can help in {c.name}</h2>
          <ul className="mt-8 grid gap-4 md:grid-cols-2">
            {c.help.map(([t, d]) => (
              <li key={t} className="flex gap-3 rounded-lg bg-white p-5"><CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-[#D9232E]" aria-hidden="true" /><span><strong className="block text-lg">{t}</strong><span className="text-base text-neutral-700">{d}</span></span></li>
            ))}
          </ul>
          <p className="mt-6 text-base text-neutral-700">We don't offer electrical, plumbing, gas, HVAC, structural or demolition work. See all <Link to="/services" className="font-bold underline">services</Link>.</p>
        </div>
      </section>
      <section className="wrap grid gap-10 py-14 md:grid-cols-2">
        <div>
          <h2 className="h2">How quoting works</h2>
          <ol className="mt-6 space-y-4">{STEPS.map(([Icon, t, d], i) => <li key={t} className="flex gap-4"><span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#171717] text-white"><Icon className="h-6 w-6" aria-hidden="true" /></span><span><strong className="text-lg">{i + 1}. {t}</strong><span className="block text-base text-neutral-700">{d}</span></span></li>)}</ol>
          <p className="mt-6 flex gap-2 rounded-md bg-[#F5F5F5] p-4 text-base" data-testid="city-travel-note"><Truck className="h-6 w-6 shrink-0" aria-hidden="true" />{TRAVEL_NOTE}</p>
        </div>
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold uppercase"><Lightbulb className="h-6 w-6 text-[#D9232E]" aria-hidden="true" />Good to know</h2>
          <ul className="mt-4 list-disc space-y-2 pl-6 text-base text-neutral-800">{c.tips.map((t) => <li key={t}>{t}</li>)}</ul>
          {zips.length > 0 && <p className="mt-4 text-sm text-neutral-600">ZIP codes we list for {c.name}: {zips.join(", ")}. {OUTSIDE_NOTE}</p>}
          <h2 className="mt-8 text-2xl font-bold uppercase">{c.name} questions</h2>
          <FaqList items={c.faq} />
        </div>
      </section>
      <section className="wrap pb-14">
        <h2 className="text-xl font-bold uppercase">Other areas we serve</h2>
        <ul className="mt-3 flex flex-wrap gap-2">{CITIES.filter((x) => x.slug !== c.slug).map((x) => <li key={x.slug}><Link to={`/service-areas/${x.slug}`} className="inline-flex min-h-[44px] items-center rounded-full border-2 border-[#171717] px-4 font-bold hover:bg-[#171717] hover:text-white" data-testid={`city-link-${x.slug}`}>{x.name}</Link></li>)}</ul>
      </section>
      <FinalCta title={`Tell us what you need in ${c.name}. We'll send clear pricing.`} />
    </div>
  );
}
